import {type ImageMetadata} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {useNaturalDimensions} from '../../util/media';
import {getImageMedia, type LoadedMediaCardProps} from '../MediaCard';
import {resolveMediaSize} from './resolveMediaSize';
import {getImageInfo} from './utils';

type ImageCardMediaProps = Pick<
  LoadedMediaCardProps,
  | 'tileMedia'
  | 'mediaKey'
  | 'mediaIndex'
  | 'mediaWidth'
  | 'mediaHeight'
  | 'runSignature'
>;

/** Shares image dimensions with a parent viewport without reporting them through an effect. */
export function useImageCardMedia({
  tileMedia,
  mediaKey,
  mediaIndex,
  mediaWidth,
  mediaHeight,
  runSignature,
}: ImageCardMediaProps) {
  const {historyRow, thumbnails} = tileMedia;
  const [naturalDimensions, imgRef] = useNaturalDimensions();
  const mediaMetadata = useMemo(
    () =>
      getImageMedia({historyRow, mediaKey}, mediaIndex) as ImageMetadata | null,
    [historyRow, mediaKey, mediaIndex]
  );
  const isSprite =
    getImageInfo({
      imgMedia: mediaMetadata,
      step: tileMedia.step,
      mediaIndex,
      mediaKey,
      runSignature,
    }).type === 'sprite';

  const mediaSize = useMemo(
    () =>
      resolveMediaSize({
        isSprite,
        thumbnails,
        naturalDimensions,
        mediaWidth,
        mediaHeight,
      }),
    [isSprite, naturalDimensions, mediaWidth, mediaHeight, thumbnails]
  );

  return useMemo(
    () => ({mediaMetadata, isSprite, naturalDimensions, imgRef, mediaSize}),
    [mediaMetadata, isSprite, naturalDimensions, imgRef, mediaSize]
  );
}
